import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { signWalletMessageForAddress } from "../wallet/walletClient";
import { api } from "../lib/api";
import { useAuth } from "../auth/useAuth";
import {
    getPlanLabel,
    isPaidPlan,
    normalizePlanCode,
} from "../components/billing/plans";

function safeApiError(err, fallback = "Something went wrong.") {
    return (
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        fallback
    );
}

function isEvmAddress(value) {
    return /^0x[a-fA-F0-9]{40}$/.test(String(value || "").trim());
}

const PENDING_CLAIM_STORAGE_KEY = "cf_pending_wallet_claim";

function withTimeout(promise, ms, message) {
    let timer;

    const timeoutPromise = new Promise((_, reject) => {
        timer = setTimeout(() => {
            reject(new Error(message));
        }, ms);
    });

    return Promise.race([
        promise.finally(() => clearTimeout(timer)),
        timeoutPromise,
    ]);
}

function cn(...values) {
    return values.filter(Boolean).join(" ");
}

function Surface({ children, className = "" }) {
    return (
        <div
            className={cn(
                "rounded-[1.75rem] border border-slate-200/80 bg-white shadow-[0_18px_55px_rgba(15,23,42,0.06)]",
                className,
            )}
        >
            {children}
        </div>
    );
}

function SectionHeader({ eyebrow, title, subtitle, right }) {
    return (
        <div className="flex flex-col gap-4 border-b border-slate-100 px-6 py-5 sm:flex-row sm:items-start sm:justify-between">
            <div>
                {eyebrow ? (
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#9A7A32]">
                        {eyebrow}
                    </div>
                ) : null}

                <h2 className="mt-2 text-lg font-semibold tracking-tight text-slate-950">
                    {title}
                </h2>

                {subtitle ? (
                    <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">
                        {subtitle}
                    </p>
                ) : null}
            </div>

            {right ? <div className="shrink-0">{right}</div> : null}
        </div>
    );
}

function Notice({ tone = "neutral", children }) {
    const tones = {
        neutral: "border-slate-200 bg-slate-50 text-slate-700",
        warning: "border-amber-200 bg-amber-50 text-amber-900",
        error: "border-rose-200 bg-rose-50 text-rose-800",
        success: "border-emerald-200 bg-emerald-50 text-emerald-800",
        blue: "border-sky-200 bg-sky-50 text-sky-900",
    };

    return (
        <div
            className={cn(
                "rounded-2xl border px-4 py-3 text-sm leading-6",
                tones[tone] || tones.neutral,
            )}
        >
            {children}
        </div>
    );
}

function Badge({ children, tone = "neutral" }) {
    const tones = {
        neutral: "border-slate-200 bg-slate-50 text-slate-700",
        gold: "border-[#E6C978]/50 bg-[#FFF8E1] text-[#7A5A12]",
        green: "border-emerald-200 bg-emerald-50 text-emerald-700",
        blue: "border-sky-200 bg-sky-50 text-sky-700",
        red: "border-rose-200 bg-rose-50 text-rose-700",
        dark: "border-slate-700 bg-slate-950 text-white",
    };

    return (
        <span
            className={cn(
                "inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium",
                tones[tone] || tones.neutral,
            )}
        >
            {children}
        </span>
    );
}

function FieldLabel({ children }) {
    return (
        <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
            {children}
        </label>
    );
}

function TextInput({ className = "", ...props }) {
    return (
        <input
            {...props}
            className={cn(
                "mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-950 outline-none transition placeholder:text-slate-400",
                "focus:border-[#C59B45] focus:ring-4 focus:ring-[#E6C978]/20",
                className,
            )}
        />
    );
}

function PrimaryButton({ children, className = "", ...props }) {
    return (
        <button
            {...props}
            className={cn(
                "inline-flex items-center justify-center rounded-2xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(15,23,42,0.18)] transition",
                "hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60",
                className,
            )}
        >
            {children}
        </button>
    );
}

function SecondaryButton({ children, className = "", ...props }) {
    return (
        <button
            {...props}
            className={cn(
                "inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition",
                "hover:border-[#C59B45]/50 hover:bg-[#FFF8E1]/60 disabled:cursor-not-allowed disabled:opacity-60",
                className,
            )}
        >
            {children}
        </button>
    );
}

function SecondaryLink({ children, className = "", ...props }) {
    return (
        <Link
            {...props}
            className={cn(
                "inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition",
                "hover:border-[#C59B45]/50 hover:bg-[#FFF8E1]/60",
                className,
            )}
        >
            {children}
        </Link>
    );
}

function DangerButton({ children, className = "", ...props }) {
    return (
        <button
            {...props}
            className={cn(
                "inline-flex items-center justify-center rounded-2xl border border-rose-200 bg-white px-4 py-2.5 text-sm font-medium text-rose-700 shadow-sm transition",
                "hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60",
                className,
            )}
        >
            {children}
        </button>
    );
}

export default function AccountSettings() {
    const {
        isAuthed,
        address,
        role,
        profile: authProfile,
        email: authEmail,
    } = useAuth();

    const [profile, setProfile] = useState(authProfile || null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileError, setProfileError] = useState("");

    const [usage, setUsage] = useState(null);
    const [usageLoading, setUsageLoading] = useState(false);
    const [usageError, setUsageError] = useState("");
    const [portalLoading, setPortalLoading] = useState(false);
    const [portalError, setPortalError] = useState("");

    const [emailInput, setEmailInput] = useState(authEmail || "");
    const [emailSaving, setEmailSaving] = useState(false);
    const [emailSaveError, setEmailSaveError] = useState("");
    const [emailSaveSuccess, setEmailSaveSuccess] = useState("");

    const [wallets, setWallets] = useState([]);
    const [walletLimits, setWalletLimits] = useState({
        planCode: "free",
        maxWallets: 1,
        currentWallets: 0,
    });
    const [walletsLoading, setWalletsLoading] = useState(false);
    const [walletsError, setWalletsError] = useState("");
    const [walletActionLoadingId, setWalletActionLoadingId] = useState("");

    const [linkWalletAddress, setLinkWalletAddress] = useState("");
    const [linkWalletLabel, setLinkWalletLabel] = useState("");

    const [walletActionState, setWalletActionState] = useState("idle");
    // idle | current_wallet | already_linked | linkable | claimable | blocked_paid_source | wallet_limit_reached

    const [walletActionMode, setWalletActionMode] = useState("");
    // "" | "link" | "claim"

    const [walletActionMessage, setWalletActionMessage] = useState("");
    const [walletActionNonce, setWalletActionNonce] = useState("");
    const [walletActionIssuedAt, setWalletActionIssuedAt] = useState("");
    const [walletActionRequestId, setWalletActionRequestId] = useState("");

    const [walletResolveLoading, setWalletResolveLoading] = useState(false);
    const [walletActionRequestLoading, setWalletActionRequestLoading] = useState(false);
    const [walletActionConfirmLoading, setWalletActionConfirmLoading] = useState(false);

    const [linkWalletError, setLinkWalletError] = useState("");
    const [linkWalletSuccess, setLinkWalletSuccess] = useState("");

    const loadProfile = useCallback(async () => {
        if (!isAuthed) {
            setProfile(null);
            setProfileError("");
            setEmailInput("");
            return;
        }

        setProfileLoading(true);
        setProfileError("");

        try {
            const res = await api.get("/account/profile", {
                headers: { "Cache-Control": "no-store" },
            });

            const nextProfile = res?.data?.profile || null;
            setProfile(nextProfile);
            setEmailInput(String(nextProfile?.email || "").trim());
        } catch (err) {
            setProfile(null);
            setProfileError(safeApiError(err, "Failed to load account profile."));
        } finally {
            setProfileLoading(false);
        }
    }, [isAuthed]);

    const loadUsage = useCallback(async () => {
        if (!isAuthed) {
            setUsage(null);
            setUsageError("");
            return;
        }

        setUsageLoading(true);
        setUsageError("");

        try {
            const res = await api.get("/account/usage", {
                headers: { "Cache-Control": "no-store" },
            });
            setUsage(res?.data || null);
        } catch (err) {
            setUsage(null);
            setUsageError(safeApiError(err, "Failed to load account usage."));
        } finally {
            setUsageLoading(false);
        }
    }, [isAuthed]);

    const loadWallets = useCallback(async () => {
        if (!isAuthed) {
            setWallets([]);
            setWalletsError("");
            return;
        }

        setWalletsLoading(true);
        setWalletsError("");

        try {
            const res = await api.get("/account/wallets", {
                headers: { "Cache-Control": "no-store" },
            });

            const nextWallets = Array.isArray(res?.data?.wallets) ? res.data.wallets : [];

            setWallets(nextWallets);
            setWalletLimits({
                planCode: String(res?.data?.walletLimits?.planCode || "free").toLowerCase(),
                maxWallets: Number(res?.data?.walletLimits?.maxWallets || 1),
                currentWallets: Number(
                    res?.data?.walletLimits?.currentWallets ?? nextWallets.length
                ),
            });
        } catch (err) {
            setWallets([]);
            setWalletLimits({
                planCode: "free",
                maxWallets: 1,
                currentWallets: 0,
            });
            setWalletsError(safeApiError(err, "Failed to load linked wallets."));
        } finally {
            setWalletsLoading(false);
        }
    }, [isAuthed]);

    const resetWalletActionFlow = useCallback(() => {
        setWalletActionState("idle");
        setWalletActionMode("");
        setWalletActionMessage("");
        setWalletActionNonce("");
        setWalletActionIssuedAt("");
        setWalletActionRequestId("");
    }, []);

    const savePendingClaimToStorage = useCallback((payload) => {
        try {
            window.localStorage.setItem(
                PENDING_CLAIM_STORAGE_KEY,
                JSON.stringify(payload),
            );
        } catch {
            // ignore storage errors
        }
    }, []);

    const clearPendingClaimFromStorage = useCallback(() => {
        try {
            window.localStorage.removeItem(PENDING_CLAIM_STORAGE_KEY);
        } catch {
            // ignore storage errors
        }
    }, []);

    const loadPendingClaimFromStorage = useCallback(() => {
        try {
            const raw = window.localStorage.getItem(PENDING_CLAIM_STORAGE_KEY);
            if (!raw) return null;
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }, []);

    useEffect(() => {
        loadProfile();
        loadUsage();
        loadWallets();
    }, [loadProfile, loadUsage, loadWallets]);

    useEffect(() => {
        const pendingClaim = loadPendingClaimFromStorage();

        if (!pendingClaim) {
            return;
        }

        const pendingWallet = String(pendingClaim?.walletAddress || "").trim();
        const pendingMode = String(pendingClaim?.mode || "").trim();
        const pendingRequestId = String(pendingClaim?.claimRequestId || "").trim();
        const pendingMessage = String(pendingClaim?.message || "").trim();
        const pendingNonce = String(pendingClaim?.nonce || "").trim();
        const pendingIssuedAt = String(pendingClaim?.issuedAt || "").trim();

        const normalizedSession = String(address || "").trim().toLowerCase();
        const normalizedPending = pendingWallet.toLowerCase();

        if (
            pendingMode === "claim" &&
            pendingWallet &&
            pendingRequestId &&
            pendingMessage &&
            pendingNonce &&
            pendingIssuedAt &&
            normalizedSession &&
            normalizedSession === normalizedPending
        ) {
            setLinkWalletAddress(pendingWallet);
            setWalletActionState("claimable");
            setWalletActionMode("claim");
            setWalletActionRequestId(pendingRequestId);
            setWalletActionMessage(pendingMessage);
            setWalletActionNonce(pendingNonce);
            setWalletActionIssuedAt(pendingIssuedAt);
        }
    }, [address, loadPendingClaimFromStorage]);

    useEffect(() => {
        if (!isAuthed) {
            return;
        }

        setWalletsError("");
        setUsageError("");
        setProfileError("");
        setLinkWalletError("");
        setLinkWalletSuccess("");

        // Do not blindly reset the wallet action flow on wallet switch,
        // because a pending claim request may need to survive logout/login.
        const pendingClaim = loadPendingClaimFromStorage();

        if (!pendingClaim) {
            resetWalletActionFlow();
        }

        loadProfile();
        loadUsage();
        loadWallets();
    }, [
        isAuthed,
        address,
        loadProfile,
        loadUsage,
        loadWallets,
        resetWalletActionFlow,
        loadPendingClaimFromStorage,
    ]);

    const handleSaveEmail = useCallback(async () => {
        if (!isAuthed) {
            setEmailSaveError("Sign in to save your email.");
            setEmailSaveSuccess("");
            return;
        }

        setEmailSaving(true);
        setEmailSaveError("");
        setEmailSaveSuccess("");

        try {
            const res = await api.patch("/account/profile", {
                email: String(emailInput || "").trim(),
            });

            const nextProfile = res?.data?.profile || null;
            setProfile(nextProfile);
            setEmailInput(String(nextProfile?.email || "").trim());
            setEmailSaveSuccess("Email saved successfully.");
        } catch (err) {
            setEmailSaveError(safeApiError(err, "Failed to save email."));
            setEmailSaveSuccess("");
        } finally {
            setEmailSaving(false);
        }
    }, [isAuthed, emailInput]);

    const handleOpenBillingPortal = useCallback(async () => {
        setPortalLoading(true);
        setPortalError("");

        try {
            const res = await api.post("/billing/portal-session");
            const url = String(res?.data?.url || "").trim();

            if (!url) {
                throw new Error("Missing billing portal URL.");
            }

            window.location.href = url;
        } catch (err) {
            setPortalError(safeApiError(err, "Failed to open billing portal."));
        } finally {
            setPortalLoading(false);
        }
    }, []);

    const handleSetPrimaryWallet = useCallback(
        async (walletId) => {
            if (!isAuthed || !walletId) return;

            setWalletActionLoadingId(walletId);
            setWalletsError("");

            try {
                await api.post("/account/wallets/primary", { walletId });
                await Promise.all([loadWallets(), loadProfile(), loadUsage()]);
            } catch (err) {
                setWalletsError(
                    safeApiError(err, "Failed to update primary wallet."),
                );
            } finally {
                setWalletActionLoadingId("");
            }
        },
        [isAuthed, loadWallets, loadProfile, loadUsage],
    );

    const handleRemoveWallet = useCallback(
        async (walletId) => {
            if (!isAuthed || !walletId) return;

            setWalletActionLoadingId(walletId);
            setWalletsError("");

            try {
                await api.delete(`/account/wallets/${walletId}`);

                setWallets((prev) => prev.filter((wallet) => wallet.id !== walletId));
                setWalletLimits((prev) => ({
                    ...prev,
                    currentWallets: Math.max(0, Number(prev.currentWallets || 0) - 1),
                }));

                setLinkWalletError("");
                setLinkWalletSuccess("Wallet removed successfully.");

                await Promise.all([loadWallets(), loadProfile(), loadUsage()]);
            } catch (err) {
                setWalletsError(
                    safeApiError(err, "Failed to remove wallet."),
                );
            } finally {
                setWalletActionLoadingId("");
            }
        },
        [isAuthed, loadWallets, loadProfile, loadUsage],
    );

    const handleResolveWalletAction = useCallback(async () => {
        const walletAddress = String(linkWalletAddress || "").trim();

        const normalizedInputAddress = walletAddress.toLowerCase();
        const normalizedCurrentAddress = String(address || "").trim().toLowerCase();

        if (!isEvmAddress(walletAddress)) {
            setLinkWalletError("Enter a valid wallet address.");
            setLinkWalletSuccess("");
            resetWalletActionFlow();
            return;
        }

        if (normalizedInputAddress === normalizedCurrentAddress) {
            setWalletActionState("current_wallet");
            setLinkWalletError("This wallet is already your current signed-in wallet.");
            setLinkWalletSuccess("");
            resetWalletActionFlow();
            return;
        }

        const alreadyLinked = wallets.some(
            (wallet) =>
                String(wallet?.address || "").trim().toLowerCase() === normalizedInputAddress,
        );

        if (alreadyLinked) {
            setWalletActionState("already_linked");
            setLinkWalletError("This wallet is already linked to your account.");
            setLinkWalletSuccess("");
            resetWalletActionFlow();
            return;
        }

        if (!isAuthed) {
            setLinkWalletError("Sign in before managing another wallet.");
            setLinkWalletSuccess("");
            resetWalletActionFlow();
            return;
        }

        setWalletResolveLoading(true);
        setLinkWalletError("");
        setLinkWalletSuccess("");
        resetWalletActionFlow();

        try {
            const res = await api.post("/account/wallets/resolve", {
                walletAddress,
            });

            const state = String(res?.data?.state || "idle").trim();
            const message = String(res?.data?.message || "").trim();
            const limits = res?.data?.walletLimits || {};

            setWalletActionState(state);
            setWalletLimits({
                planCode: String(limits?.planCode || walletLimits.planCode || "free").toLowerCase(),
                maxWallets: Number(limits?.maxWallets || walletLimits.maxWallets || 1),
                currentWallets: Number(limits?.currentWallets || walletLimits.currentWallets || wallets.length),
            });

            if (state === "linkable") {
                setLinkWalletSuccess(message || "This wallet can be linked.");
            } else if (state === "claimable") {
                setLinkWalletSuccess(
                    message ||
                    "This wallet was previously used on another free account and can be claimed.",
                );

            } else if (state === "wallet_limit_reached") {
                setLinkWalletError("");
                setLinkWalletSuccess("");

            } else if (state === "already_linked") {
                setLinkWalletError(message || "This wallet is already linked.");
            } else if (state === "current_wallet") {
                setLinkWalletError(message || "This wallet is already your current wallet.");
            }
        } catch (err) {
            setLinkWalletError(
                safeApiError(err, "Failed to resolve wallet action."),
            );
            setLinkWalletSuccess("");
            resetWalletActionFlow();
        } finally {
            setWalletResolveLoading(false);
        }
    }, [isAuthed, linkWalletAddress, address, wallets, resetWalletActionFlow, walletLimits]);

    const handleCreateWalletActionRequest = useCallback(async () => {
        const walletAddress = String(linkWalletAddress || "").trim();

        if (!isEvmAddress(walletAddress)) {
            setLinkWalletError("Enter a valid wallet address.");
            setLinkWalletSuccess("");
            return;
        }

        if (!isAuthed) {
            setLinkWalletError("Sign in before managing another wallet.");
            setLinkWalletSuccess("");
            return;
        }

        if (walletActionState !== "linkable" && walletActionState !== "claimable") {
            setLinkWalletError("Check the wallet first before continuing.");
            setLinkWalletSuccess("");
            return;
        }

        const mode = walletActionState === "claimable" ? "claim" : "link";
        const route =
            mode === "claim"
                ? "/account/wallets/claim/request"
                : "/account/wallets/link/request";

        setWalletActionRequestLoading(true);
        setLinkWalletError("");
        setLinkWalletSuccess("");
        setWalletActionMode("");
        setWalletActionMessage("");
        setWalletActionNonce("");
        setWalletActionIssuedAt("");
        setWalletActionRequestId("");

        try {
            const res = await api.post(route, {
                walletAddress,
            });

            const nextMessage = String(res?.data?.message || "");
            const nextNonce = String(res?.data?.nonce || "");
            const nextIssuedAt = String(res?.data?.issuedAt || "");
            const nextRequestId = String(res?.data?.claimRequestId || "");

            setWalletActionMode(mode);
            setWalletActionMessage(nextMessage);
            setWalletActionNonce(nextNonce);
            setWalletActionIssuedAt(nextIssuedAt);
            setWalletActionRequestId(nextRequestId);

            if (mode === "claim") {
                savePendingClaimToStorage({
                    mode,
                    claimRequestId: nextRequestId,
                    walletAddress,
                    message: nextMessage,
                    nonce: nextNonce,
                    issuedAt: nextIssuedAt,
                });
            }

            setLinkWalletSuccess(
                mode === "claim"
                    ? "Claim request created. Next step: sign out, sign in with the wallet being claimed, then click Approve Claim."
                    : "Request created successfully. Next: switch to the wallet you want to add and click “Confirm Link” to finish.",
            );
        } catch (err) {
            setLinkWalletError(
                safeApiError(err, "Failed to create wallet request."),
            );
            setLinkWalletSuccess("");
            setWalletActionMode("");
            setWalletActionMessage("");
            setWalletActionNonce("");
            setWalletActionIssuedAt("");
            setWalletActionRequestId("");
            clearPendingClaimFromStorage();
        } finally {
            setWalletActionRequestLoading(false);
        }
    }, [
        isAuthed,
        linkWalletAddress,
        walletActionState,
        savePendingClaimToStorage,
        clearPendingClaimFromStorage,
    ]);

    const currentPlan = normalizePlanCode(usage?.plan || "free");
    const currentPlanLabel = getPlanLabel(currentPlan);
    const currentPlanIsPaid = isPaidPlan(currentPlan);

    const deploymentsUsed = Number(usage?.usage?.deploymentsUsed || 0);
    const deploymentsLimit = usage?.limits?.unlimited
        ? "Unlimited"
        : Number(usage?.limits?.deployments || 0);

    const forkSimulationsUsed = Number(usage?.usage?.forkSimulationsUsed || 0);
    const forkSimulationsLimit = usage?.limits?.unlimited
        ? "Unlimited"
        : Number(usage?.limits?.forkSimulations || 0);

    const includedDeployments = Number(usage?.limits?.includedDeployments || 0);
    const extraDeployments = Number(usage?.limits?.extraDeployments || 0);
    const includedForkSimulations = Number(usage?.limits?.includedForkSimulations || 0);
    const extraForkSimulations = Number(usage?.limits?.extraForkSimulations || 0);

    const billingStatus = String(usage?.billing?.status || "inactive")
        .trim()
        .toLowerCase();

    const billingCurrentPeriodEnd = usage?.billing?.currentPeriodEnd || null;
    const billingCancelAtPeriodEnd = Boolean(usage?.billing?.cancelAtPeriodEnd);

    const isCurrentSessionActionWallet =
        Boolean(String(address || "").trim()) &&
        String(address || "").trim().toLowerCase() ===
        String(linkWalletAddress || "").trim().toLowerCase();

    const canCreateWalletRequest =
        walletActionState === "linkable" || walletActionState === "claimable";

    const isPrimarySession = wallets.some(
        (w) =>
            Boolean(w?.isPrimary) &&
            String(w?.address || "").toLowerCase() === String(address || "").toLowerCase()
    );

    const hasPendingClaimApproval =
        walletActionMode === "claim" &&
        Boolean(String(walletActionRequestId || "").trim()) &&
        Boolean(String(walletActionMessage || "").trim()) &&
        Boolean(String(linkWalletAddress || "").trim());

    const showClaimApprovalOnly =
        hasPendingClaimApproval &&
        isCurrentSessionActionWallet;

    const canShowWalletManagementPanel =
        isPrimarySession &&
        (
            Number(walletLimits.currentWallets || 0) < Number(walletLimits.maxWallets || 0) ||
            hasPendingClaimApproval ||
            walletActionState === "claimable"
        );

    const canConfirmWalletAction =
        Boolean(String(linkWalletAddress || "").trim()) &&
        Boolean(walletActionMode) &&
        Boolean(walletActionMessage) &&
        Boolean(walletActionNonce) &&
        Boolean(walletActionIssuedAt) &&
        !walletResolveLoading &&
        !walletActionRequestLoading &&
        !walletActionConfirmLoading &&
        isCurrentSessionActionWallet;

    const handleConfirmWalletAction = useCallback(async () => {
        const walletAddress = String(linkWalletAddress || "").trim();

        if (!isEvmAddress(walletAddress)) {
            setLinkWalletError("Enter a valid wallet address before confirming.");
            setLinkWalletSuccess("");
            return;
        }

        if (!isAuthed) {
            setLinkWalletError("Sign in before managing another wallet.");
            setLinkWalletSuccess("");
            return;
        }

        if (!walletActionMode || !walletActionMessage || !walletActionNonce || !walletActionIssuedAt) {
            setLinkWalletError("Create a wallet request first.");
            setLinkWalletSuccess("");
            return;
        }

        const normalizedSessionAddress = String(address || "").trim().toLowerCase();
        const normalizedActionAddress = walletAddress.toLowerCase();

        if (!normalizedSessionAddress || normalizedSessionAddress !== normalizedActionAddress) {
            setLinkWalletError("Switch your current signed-in session to this wallet, then confirm.");
            setLinkWalletSuccess("");
            return;
        }

        setWalletActionConfirmLoading(true);
        setLinkWalletError("");
        setLinkWalletSuccess(
            walletActionMode === "claim"
                ? "Waiting for wallet signature..."
                : "Waiting for wallet confirmation..."
        );

        try {
            console.log("[wallet-action] starting signature", {
                mode: walletActionMode,
                walletAddress,
                requestId: walletActionRequestId || null,
            });

            const signature = await withTimeout(
                signWalletMessageForAddress(walletAddress, walletActionMessage),
                60000,
                "Wallet signature timed out. Open your wallet and approve the request, then try again."
            );

            console.log("[wallet-action] signature received", {
                mode: walletActionMode,
                walletAddress,
            });

            setLinkWalletSuccess(
                walletActionMode === "claim"
                    ? "Signature received. Finishing claim..."
                    : "Signature received. Finishing wallet link..."
            );

            const route =
                walletActionMode === "claim"
                    ? "/account/wallets/claim/approve"
                    : "/account/wallets/link/confirm";

            const payload = {
                walletAddress,
                signature,
                label: String(linkWalletLabel || "").trim() || null,
                ...(walletActionMode === "claim"
                    ? { claimRequestId: walletActionRequestId }
                    : {}),
            };

            console.log("[wallet-action] posting confirm request", {
                route,
                walletAddress,
                requestId: walletActionRequestId || null,
            });

            await withTimeout(
                api.post(route, payload, { timeout: 20000 }),
                25000,
                "Server confirmation timed out. Please try again."
            );

            console.log("[wallet-action] confirm request completed", {
                route,
                walletAddress,
            });

            setLinkWalletSuccess(
                walletActionMode === "claim"
                    ? "Wallet claimed successfully. Refreshing your account session..."
                    : "Wallet linked successfully."
            );

            setLinkWalletError("");
            setLinkWalletAddress("");
            setLinkWalletLabel("");
            clearPendingClaimFromStorage();
            resetWalletActionFlow();

            if (walletActionMode === "claim") {
                try {
                    // Force the app to mint a fresh access token for the newly claimed account.
                    // Keep refresh token intact; only remove stale access token.
                    window.localStorage.removeItem("cf_access_token");
                } catch {
                    // ignore storage errors
                }

                window.location.reload();
                return;
            }

            await Promise.all([loadWallets(), loadProfile(), loadUsage()]);
        } catch (err) {
            console.error("[wallet-action] confirm failed", err);
            setLinkWalletError(
                safeApiError(err, "Failed to confirm wallet action.")
            );
            setLinkWalletSuccess("");
        } finally {
            setWalletActionConfirmLoading(false);
        }
    }, [
        isAuthed,
        address,
        linkWalletAddress,
        linkWalletLabel,
        walletActionMode,
        walletActionMessage,
        walletActionNonce,
        walletActionIssuedAt,
        walletActionRequestId,
        clearPendingClaimFromStorage,
        loadWallets,
        loadProfile,
        loadUsage,
        resetWalletActionFlow,
    ]);

    if (!isAuthed) {
        return (
            <div className="min-h-screen bg-[#F7F8FA] px-4 py-8 text-slate-950">
                <div className="mx-auto max-w-5xl">
                    <Surface className="overflow-hidden">
                        <div className="relative overflow-hidden bg-[#050713] px-6 py-8 text-white sm:px-8">
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(197,155,69,0.22),transparent_32%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.10),transparent_30%)]" />

                            <div className="relative max-w-2xl">
                                <div className="inline-flex rounded-full border border-[#E6C978]/30 bg-[#E6C978]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#E6C978]">
                                    Account Access
                                </div>

                                <h1 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
                                    Sign in to manage your Krionex account.
                                </h1>

                                <p className="mt-3 text-sm leading-7 text-slate-300">
                                    Connect and sign in from the top header to manage billing, linked wallets,
                                    usage limits, and account identity.
                                </p>

                                <div className="mt-6">
                                    <SecondaryLink
                                        to="/upgrade"
                                        className="border-white/15 bg-white/10 text-white hover:bg-white/15"
                                    >
                                        View Plans
                                    </SecondaryLink>
                                </div>
                            </div>
                        </div>
                    </Surface>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F7F8FA] px-4 py-8 text-slate-950">
            <div className="mx-auto max-w-6xl space-y-6">
                <Surface className="relative overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(197,155,69,0.10),transparent_30%),linear-gradient(to_bottom,#ffffff,#fafafa)]" />

                    <div className="relative px-6 py-7 sm:px-8">
                        <div className="inline-flex items-center rounded-full border border-[#E6C978]/50 bg-[#FFF8E1] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7A5A12]">
                            KRIONEX Account
                        </div>

                        <div className="mt-4 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                            <div>
                                <h1 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                                    Account Settings
                                </h1>
                                <p className="mt-2 max-w-2xl text-sm leading-7 text-slate-500">
                                    Manage your account identity, billing contact, current plan, and wallet-linked
                                    access from one secure workspace.
                                </p>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                <Badge tone={currentPlanIsPaid ? "gold" : "neutral"}>
                                    {currentPlanLabel}
                                </Badge>

                                <Badge tone="neutral">
                                    {String(profile?.role || role || "user").toLowerCase() === "owner"
                                        ? "Owner"
                                        : String(profile?.role || role || "user").toLowerCase() === "admin"
                                            ? "Admin"
                                            : "Workspace Member"}
                                </Badge>
                            </div>
                        </div>
                    </div>
                </Surface>

                <div className="grid gap-6 lg:grid-cols-2">
                    <Surface className="overflow-hidden">
                        <SectionHeader
                            eyebrow="Identity"
                            title="Profile"
                            subtitle="Manage the wallet and billing email connected to this workspace."
                        />

                        <div className="space-y-5 px-6 py-5">
                            {profileLoading && (
                                <Notice>
                                    Loading your profile…
                                </Notice>
                            )}

                            {profileError && (
                                <Notice tone="warning">
                                    {profileError}
                                </Notice>
                            )}

                            {!profileLoading && !profileError && (
                                <>
                                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                        <div className="text-xs font-medium text-slate-500">
                                            Signed-in wallet
                                        </div>
                                        <div className="mt-2 break-all text-sm font-semibold text-slate-950">
                                            {address || profile?.walletAddress || "-"}
                                        </div>
                                    </div>

                                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                        <div className="text-xs font-medium text-slate-500">
                                            Workspace access
                                        </div>
                                        <div className="mt-2 text-sm font-semibold text-slate-950">
                                            {String(profile?.role || role || "user").toLowerCase() === "owner"
                                                ? "Owner"
                                                : String(profile?.role || role || "user").toLowerCase() === "admin"
                                                    ? "Admin"
                                                    : "Workspace Member"}
                                        </div>
                                    </div>

                                    <div>
                                        <FieldLabel>Billing email</FieldLabel>
                                        <TextInput
                                            type="email"
                                            value={emailInput}
                                            onChange={(e) => {
                                                setEmailInput(e.target.value);
                                                setEmailSaveError("");
                                                setEmailSaveSuccess("");
                                            }}
                                            placeholder="you@example.com"
                                        />
                                    </div>


                                    {emailSaveError && (
                                        <Notice tone="error">
                                            {emailSaveError}
                                        </Notice>
                                    )}

                                    {emailSaveSuccess && (
                                        <Notice tone="success">
                                            {emailSaveSuccess}
                                        </Notice>
                                    )}

                                    <div>
                                        <PrimaryButton
                                            type="button"
                                            onClick={handleSaveEmail}
                                            disabled={emailSaving}
                                        >
                                            {emailSaving ? "Saving..." : "Save Email"}
                                        </PrimaryButton>
                                    </div>
                                </>
                            )}
                        </div>
                    </Surface>

                    <Surface className="overflow-hidden">
                        <SectionHeader
                            eyebrow="Billing"
                            title="Plan & Usage"
                            subtitle="Review your current plan, usage limits, and subscription status."
                        />

                        <div className="space-y-5 px-6 py-5">
                            {usageLoading && (
                                <Notice>
                                    Loading your account usage…
                                </Notice>
                            )}

                            {usageError && (
                                <Notice tone="warning">
                                    {usageError}
                                </Notice>
                            )}

                            {!usageLoading && !usageError && (
                                <>
                                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                            <div className="text-xs font-medium text-slate-500">Current plan</div>
                                            <div className="mt-2 text-lg font-semibold text-slate-950">
                                                {currentPlanLabel}
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                            <div className="text-xs font-medium text-slate-500">Deployment usage</div>
                                            <div className="mt-2 text-lg font-semibold text-slate-950">
                                                {deploymentsUsed} / {deploymentsLimit}
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                            <div className="text-xs font-medium text-slate-500">Preflight usage</div>
                                            <div className="mt-2 text-lg font-semibold text-slate-950">
                                                {forkSimulationsUsed} / {forkSimulationsLimit}
                                            </div>
                                        </div>

                                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                            <div className="text-xs font-medium text-slate-500">Billing status</div>
                                            <div className="mt-2 text-lg font-semibold capitalize text-slate-950">
                                                {billingStatus.replaceAll("_", " ")}
                                            </div>
                                        </div>
                                    </div>

                                    {currentPlanIsPaid && !usage?.limits?.unlimited && (
                                        <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs leading-5 text-slate-500">
                                            Included this billing period: {includedDeployments || 0} deployments +{" "}
                                            {includedForkSimulations || 0} preflight simulations
                                            {(extraDeployments > 0 || extraForkSimulations > 0) && (
                                                <>
                                                    {" "}
                                                    · Deployment Capacity Pack: {extraDeployments} deployments + {extraForkSimulations} preflight simulations
                                                </>
                                            )}
                                        </div>
                                    )}


                                    {billingCurrentPeriodEnd && (
                                        <div className="text-xs text-slate-500">
                                            Current period ends: {new Date(billingCurrentPeriodEnd).toLocaleString()}
                                        </div>
                                    )}

                                    {billingCancelAtPeriodEnd && (
                                        <Notice tone="warning">
                                            Your subscription is set to cancel at the end of the current billing period.
                                        </Notice>
                                    )}

                                    {portalError && (
                                        <Notice tone="error">
                                            {portalError}
                                        </Notice>
                                    )}

                                    <div className="flex flex-wrap gap-3 pt-2">
                                        <SecondaryLink to="/upgrade">
                                            View Plans
                                        </SecondaryLink>

                                        {currentPlanIsPaid && (
                                            <PrimaryButton
                                                type="button"
                                                onClick={handleOpenBillingPortal}
                                                disabled={portalLoading}
                                            >
                                                {portalLoading ? "Opening..." : "Manage Billing"}
                                            </PrimaryButton>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </Surface>
                </div>
                <Surface className="overflow-hidden">
                    <SectionHeader
                        eyebrow="Wallet Access"
                        title="Linked Wallets"
                        subtitle="All linked wallets share one Krionex account, billing profile, deployment allowance, preflight allowance, and plan access."
                        right={
                            <Badge tone="gold">
                                {walletLimits.currentWallets} / {walletLimits.maxWallets} wallet slots
                            </Badge>
                        }
                    />

                    <div className="space-y-5 px-6 py-5">
                        {walletsLoading && (
                            <Notice>
                                Loading linked wallets…
                            </Notice>
                        )}

                        {walletsError && (
                            <Notice tone="warning">
                                {walletsError}
                            </Notice>
                        )}

                        {!walletsLoading && !walletsError && wallets.length === 0 && (
                            <Notice>
                                No linked wallets found for this account yet.
                            </Notice>
                        )}

                        {!walletsLoading && wallets.length > 0 && (
                            <div className="space-y-3">
                                {wallets.map((wallet) => {
                                    const isPrimary = Boolean(wallet?.isPrimary);
                                    const hasMultiplePrimary = wallets.filter(w => w.isPrimary).length > 1;
                                    const isCurrent = String(wallet?.address || "").toLowerCase() ===
                                        String(address || "").toLowerCase();
                                    const isSessionPrimaryWallet = wallets.some(
                                        (w) =>
                                            Boolean(w?.isPrimary) &&
                                            String(w?.address || "").toLowerCase() === String(address || "").toLowerCase()
                                    );

                                    return (
                                        <div
                                            key={wallet.id}
                                            className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                                        >
                                            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                                                <div className="min-w-0 space-y-2">
                                                    <div className="break-all text-sm font-semibold text-slate-950">
                                                        {wallet.address}
                                                    </div>

                                                    <div className="flex flex-wrap gap-2">
                                                        {isPrimary && !hasMultiplePrimary && (
                                                            <Badge tone="gold">
                                                                Primary
                                                            </Badge>
                                                        )}

                                                        {isCurrent && (
                                                            <Badge tone="green">
                                                                Current session
                                                            </Badge>
                                                        )}

                                                        {!wallet.isActive && (
                                                            <Badge>
                                                                Inactive
                                                            </Badge>
                                                        )}
                                                    </div>

                                                    <div className="text-xs text-slate-500">
                                                        Added:{" "}
                                                        {wallet.createdAt
                                                            ? new Date(wallet.createdAt).toLocaleString()
                                                            : "-"}
                                                    </div>
                                                </div>

                                                <div className="flex flex-wrap gap-2">
                                                    {isSessionPrimaryWallet && !isPrimary && (
                                                        <SecondaryButton
                                                            type="button"
                                                            onClick={() => handleSetPrimaryWallet(wallet.id)}
                                                            disabled={walletActionLoadingId === wallet.id}
                                                        >
                                                            {walletActionLoadingId === wallet.id
                                                                ? "Updating..."
                                                                : "Make Primary"}
                                                        </SecondaryButton>
                                                    )}

                                                    {isSessionPrimaryWallet && !isPrimary && (
                                                        <DangerButton
                                                            type="button"
                                                            onClick={() => handleRemoveWallet(wallet.id)}
                                                            disabled={walletActionLoadingId === wallet.id}
                                                        >
                                                            {walletActionLoadingId === wallet.id
                                                                ? "Removing..."
                                                                : "Remove"}
                                                        </DangerButton>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {canShowWalletManagementPanel ? (
                            <div className="space-y-5 rounded-[1.5rem] border border-[#E6C978]/40 bg-[#FFF8E1]/50 p-5">
                                {showClaimApprovalOnly ? (
                                    <>
                                        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                            <div className="space-y-2">
                                                <Badge tone="green">Step 2 ready</Badge>

                                                <div className="text-base font-semibold text-slate-950">
                                                    Approve wallet claim
                                                </div>

                                                <div className="text-sm leading-6 text-slate-600">
                                                    Krionex needs a signature from the wallet being claimed before it can join this account.
                                                </div>
                                            </div>

                                            <Notice>
                                                Next step: click Approve Claim and confirm the signature in your wallet.
                                            </Notice>

                                            <div className="mt-4 grid gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 md:grid-cols-2">
                                                <div className="break-all">
                                                    <span className="font-medium text-slate-800">Request nonce:</span>{" "}
                                                    {walletActionNonce || "-"}
                                                </div>
                                                <div>
                                                    <span className="font-medium text-slate-800">Created at:</span>{" "}
                                                    {walletActionIssuedAt || "-"}
                                                </div>
                                            </div>

                                            <details className="mt-4 rounded-2xl border border-slate-200 bg-slate-50">
                                                <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-slate-700">
                                                    View signature request details
                                                </summary>
                                                <pre className="whitespace-pre-wrap break-words border-t border-slate-200 p-4 text-xs text-slate-700">
                                                    {walletActionMessage}
                                                </pre>
                                            </details>

                                            <div className="mt-4 flex flex-wrap gap-3">
                                                <PrimaryButton
                                                    type="button"
                                                    onClick={handleConfirmWalletAction}
                                                    disabled={!canConfirmWalletAction && !walletActionConfirmLoading}
                                                    className={canConfirmWalletAction ? "bg-emerald-700 hover:bg-emerald-800" : ""}
                                                >
                                                    {walletActionConfirmLoading ? "Approving..." : "Approve Claim"}
                                                </PrimaryButton>
                                            </div>

                                            {linkWalletError && (
                                                <div className="mt-4">
                                                    <Notice tone="error">
                                                        {linkWalletError}
                                                    </Notice>
                                                </div>
                                            )}

                                            {linkWalletSuccess && (
                                                <div className="mt-4">
                                                    <Notice tone="success">
                                                        {linkWalletSuccess}
                                                    </Notice>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div>
                                            <div className="text-sm font-semibold text-slate-950">
                                                Add or claim wallet access
                                            </div>
                                            <div className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">
                                                Add a new wallet to this account, or claim a wallet previously used on a separate free Krionex account. Your current plan allows up to {walletLimits.maxWallets} linked wallet{walletLimits.maxWallets === 1 ? "" : "s"}.
                                            </div>
                                        </div>

                                        <div className="grid gap-4 md:grid-cols-2">
                                            <div>
                                                <FieldLabel>Wallet address</FieldLabel>
                                                <TextInput
                                                    type="text"
                                                    value={linkWalletAddress}
                                                    onChange={(e) => {
                                                        setLinkWalletAddress(e.target.value);
                                                        setLinkWalletError("");
                                                        setLinkWalletSuccess("");
                                                        resetWalletActionFlow();
                                                    }}
                                                    placeholder="0x..."
                                                />
                                            </div>

                                            <div>
                                                <FieldLabel>Wallet label optional</FieldLabel>
                                                <TextInput
                                                    type="text"
                                                    value={linkWalletLabel}
                                                    onChange={(e) => {
                                                        setLinkWalletLabel(e.target.value);
                                                        setLinkWalletError("");
                                                        setLinkWalletSuccess("");
                                                    }}
                                                    placeholder="Treasury wallet, Ops wallet, etc."
                                                />
                                            </div>
                                        </div>

                                        <div className="flex flex-wrap gap-3">
                                            <PrimaryButton
                                                type="button"
                                                onClick={handleResolveWalletAction}
                                                disabled={
                                                    walletResolveLoading ||
                                                    walletActionRequestLoading ||
                                                    walletActionConfirmLoading ||
                                                    !String(linkWalletAddress || "").trim()
                                                }
                                            >
                                                {walletResolveLoading ? "Checking..." : "Check Wallet"}
                                            </PrimaryButton>

                                            <SecondaryButton
                                                type="button"
                                                onClick={handleCreateWalletActionRequest}
                                                disabled={
                                                    walletResolveLoading ||
                                                    walletActionRequestLoading ||
                                                    walletActionConfirmLoading ||
                                                    !canCreateWalletRequest
                                                }
                                                className={walletActionState === "claimable" && canCreateWalletRequest ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100" : ""}
                                            >
                                                {walletActionRequestLoading
                                                    ? "Creating..."
                                                    : walletActionState === "claimable"
                                                        ? "Create Claim Request"
                                                        : "Create Link Request"}
                                            </SecondaryButton>

                                            <PrimaryButton
                                                type="button"
                                                onClick={handleConfirmWalletAction}
                                                disabled={!canConfirmWalletAction}
                                                className={canConfirmWalletAction ? "bg-emerald-700 hover:bg-emerald-800" : ""}
                                            >
                                                {walletActionConfirmLoading
                                                    ? walletActionMode === "claim" ? "Approving..." : "Confirming..."
                                                    : walletActionMode === "claim"
                                                        ? "Approve Claim"
                                                        : "Confirm Link"}
                                            </PrimaryButton>
                                        </div>

                                        {walletActionState === "claimable" && (
                                            <Notice tone="warning">
                                                This wallet was previously used on another free Krionex account. Claiming it will move only the wallet login identity into this account. Old deployments, usage, and other account history will stay on the old account.
                                            </Notice>
                                        )}

                                        {walletActionState === "wallet_limit_reached" && (
                                            <Notice tone="error">
                                                You have reached your wallet limit for this plan. Remove a non-primary wallet first or upgrade for more wallet slots.
                                            </Notice>
                                        )}

                                        {walletActionState === "blocked_paid_source" && (
                                            <Notice tone="error">
                                                This wallet belongs to another paid Krionex account and cannot be claimed automatically.
                                            </Notice>
                                        )}

                                        {walletActionMessage && !showClaimApprovalOnly && (
                                            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                                                <div className="space-y-2">
                                                    <Badge tone="green">Step 2 ready</Badge>

                                                    <div className="text-base font-semibold text-slate-950">
                                                        {walletActionMode === "claim"
                                                            ? "Approve wallet claim"
                                                            : "Confirm wallet ownership"}
                                                    </div>

                                                    <div className="text-sm leading-6 text-slate-600">
                                                        {walletActionMode === "claim"
                                                            ? "To protect this account, Krionex needs a signature from the wallet being claimed."
                                                            : "To protect this account, Krionex needs a signature from the wallet you want to add."}
                                                    </div>
                                                </div>

                                                <div className="mt-4 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700">
                                                    {walletActionMode === "claim"
                                                        ? "Next step: switch to the wallet being claimed, then click Approve Claim and confirm the signature in your wallet."
                                                        : "Next step: switch to the wallet you want to add, then click Confirm Link and approve the signature in your wallet."}
                                                </div>

                                                <div className="mt-4 grid gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 md:grid-cols-2">
                                                    <div className="break-all">
                                                        <span className="font-medium text-slate-800">Request nonce:</span>{" "}
                                                        {walletActionNonce || "-"}
                                                    </div>
                                                    <div>
                                                        <span className="font-medium text-slate-800">Created at:</span>{" "}
                                                        {walletActionIssuedAt || "-"}
                                                    </div>
                                                </div>

                                                <details className="mt-4 rounded-2xl border border-slate-200 bg-slate-50">
                                                    <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-slate-700">
                                                        View signature request details
                                                    </summary>
                                                    <pre className="whitespace-pre-wrap break-words border-t border-slate-200 p-4 text-xs text-slate-700">
                                                        {walletActionMessage}
                                                    </pre>
                                                </details>
                                            </div>
                                        )}

                                        {walletActionMode === "claim" && walletActionRequestId && !isCurrentSessionActionWallet && (
                                            <Notice tone="warning">
                                                Claim request is ready. You are still signed in with the target account. Switch to the wallet being claimed to continue.
                                            </Notice>
                                        )}

                                        {linkWalletError && (
                                            <Notice tone="error">
                                                {linkWalletError}
                                            </Notice>
                                        )}

                                        {linkWalletSuccess && (
                                            <Notice tone="success">
                                                {linkWalletSuccess}
                                            </Notice>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : (
                            <Notice>
                                You are signed in with a linked wallet. Only the primary wallet session can add, remove, claim, or change linked wallets.
                            </Notice>
                        )}
                    </div>
                </Surface>
            </div>
        </div>
    );
}